/** @format */
import { AxiosRequestConfig } from 'axios';

import { transformTransactionSummary } from '@toucan/common/features/transaction/helpers';

import { client } from '@toucan/client/global/axios';
import { MarketCode } from '@toucan/client/global/market';
import {
    QuoteExpiryState,
    TransactionByCustomerResponse,
    TransactionState,
    TransactionVariant,
    transformExpiredQuotes,
} from '@toucan/client/global/transaction';

/**
 * Gets a list of open quotes
 */
export const getOpenQuotes = async (
    market: MarketCode,
    customerId: number,
    length: number,
    start: number
): Promise<TransactionByCustomerResponse> => {
    const request = {
        customerId,
        length,
        start,
        quoteExpiry: QuoteExpiryState.UNEXPIRED,
        hasActiveCollectionConsignment: false,
        variant: [
            TransactionVariant.PART_EXCHANGE,
            TransactionVariant.CASH_PURCHASE,
        ],
    };

    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        '/transaction-service/v2/transaction-by-customer/',
        request,
        config
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
    response.data.data = response.data.data.map((transaction: any) =>
        transformTransactionSummary(transaction, {
            itemsToSellStatesToExclude: [],
        })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
    return response.data;
};

/**
 * Gets a list of expired quotes
 */
export const getExpiredQuotes = async (
    market: MarketCode,
    customerId: number,
    length: number,
    start: number
): Promise<TransactionByCustomerResponse> => {
    const request = {
        customerId,
        length,
        start,
        quoteExpiry: QuoteExpiryState.EXPIRED,
        hasActiveCollectionConsignment: false,
        variant: [
            TransactionVariant.PART_EXCHANGE,
            TransactionVariant.CASH_PURCHASE,
        ],
    };

    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        '/transaction-service/v2/transaction-by-customer/',
        request,
        config
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
    response.data.data = response.data.data.map((transaction: any) =>
        transformExpiredQuotes(
            transformTransactionSummary(transaction, {
                itemsToSellStatesToExclude: [],
            })
        )
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
    return response.data;
};

type GetTransactionsOptions = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    allowUnknownCondition?: boolean;
    // eslint-disable-next-line @typescript-eslint/array-type -- Legacy code: do not use as an example.
    variant?: readonly TransactionVariant[];
    // eslint-disable-next-line @typescript-eslint/array-type -- Legacy code: do not use as an example.
    state?: readonly TransactionState[];
    hasActiveCollectionConsignment?: boolean;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    ignoreDeclinedOrCancelled?: boolean;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    ignoreCancelledTransactions?: boolean;
};

export const getTransactions = async (
    market: MarketCode,
    customerId: number,
    length: number,
    start: number,
    {
        allowUnknownCondition = false,
        variant = [
            TransactionVariant.PART_EXCHANGE,
            TransactionVariant.CASH_PURCHASE,
            TransactionVariant.ORDER,
        ],
        state,
        hasActiveCollectionConsignment,
        ignoreDeclinedOrCancelled = false,
        ignoreCancelledTransactions = false,
    }: GetTransactionsOptions = {}
): Promise<TransactionByCustomerResponse> => {
    const request = {
        customerId,
        length,
        start,
        variant,
        ignoreDeclinedOrCancelled,
        ignoreCancelledTransactions,
        // The backend treats this as a real three-way filter (true/false/omitted all
        // behave differently), so it can't default to a fixed boolean without changing
        // behavior for callers that rely on it being left off the request entirely.
        ...(hasActiveCollectionConsignment !== undefined
            ? { hasActiveCollectionConsignment }
            : {}),
        ...(state !== undefined ? { state } : {}),
    };

    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        '/transaction-service/v2/transaction-by-customer/',
        request,
        config
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
    response.data.data = response.data.data.map((transaction: any) =>
        transformTransactionSummary(transaction, {
            allowUnknownCondition,
        })
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
    return response.data;
};
